import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { fetchDocoonSignatories } from "store/agreementsStore";
import type { DocoonSignatory, DocoonSignatoryWithRole } from "store/agreementsStore";
import { useAppDispatch } from "store/store";
import DocoonSignatoryStep, { type CoBeneficiarySigner, toInitialSignatoryBlocks } from "./DocoonSignatoryStep";

interface IDocoonSignModal {
    open: boolean;
    agreementId: number | null;
    onClose: () => void;
    onSuccess?: (signatories: DocoonSignatoryWithRole[]) => void;
    // Stored signatories, the main one first
    initialSignatories?: DocoonSignatoryWithRole[];
    mainSignerEmail?: string;
    mainSignerNameOverride?: string;
    // Other beneficiaries signing the contract: each keeps a required block, as when creating it
    coBeneficiaries?: CoBeneficiarySigner[];
}

const DocoonSignModal = ({
    open,
    agreementId,
    onClose,
    onSuccess,
    initialSignatories,
    mainSignerEmail: mainSignerEmailProp,
    mainSignerNameOverride,
    coBeneficiaries = [],
}: IDocoonSignModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [signatairesIds, setSignatairesIds] = useState("");
    const [blockingReason, setBlockingReason] = useState<string | null>(null);
    const [selectedSignatories, setSelectedSignatories] = useState<DocoonSignatoryWithRole[]>([]);
    // increments on each open to force DocoonSignatoryStep re-mount with fresh initialMain
    const [openCount, setOpenCount] = useState(0);

    const searchDocoonSignatories = useCallback(
        async (query: string, signal?: AbortSignal): Promise<DocoonSignatory[]> => {
            const result = await dispatch(fetchDocoonSignatories({ email: query, signal }));
            if (fetchDocoonSignatories.fulfilled.match(result)) return result.payload;
            return [];
        },
        [dispatch],
    );

    useEffect(() => {
        if (!open || !agreementId) return;
        setOpenCount((c) => c + 1);
    }, [open, agreementId]);

    const initialBlocks = toInitialSignatoryBlocks(initialSignatories ?? [], coBeneficiaries);
    const { initialMain } = initialBlocks;
    const mainSignerEmail = mainSignerEmailProp || initialMain?.email || "";
    const mainSignerName =
        mainSignerNameOverride ?? (initialMain ? `${initialMain.firstName} ${initialMain.lastName}` : "");

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            mainTitle={t("DocoonSignModal.ModalTitle")}
            subTitle={t("DocoonSignModal.ModalSubtitle")}
            style={{ width: 720, maxWidth: "95vw" }}
        >
            <div className="flex flex-col gap-4">
                <DocoonSignatoryStep
                    key={openCount}
                    mainSignerEmail={mainSignerEmail}
                    mainSignerName={mainSignerName}
                    docoonSignatories={[]}
                    isLoading={false}
                    onSearch={searchDocoonSignatories}
                    onChange={(ids) => setSignatairesIds(ids)}
                    onBlockingReasonChange={setBlockingReason}
                    onSignatoriesChange={(sigs) => setSelectedSignatories(sigs)}
                    {...initialBlocks}
                    coBeneficiaries={coBeneficiaries}
                />
                <div className="h-px bg-gray-100" />
                <div className="flex justify-between">
                    <StyledButton buttonType="secondary" onClick={onClose}>
                        {t("DocoonSignModal.Cancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        onClick={() => {
                            onSuccess?.(selectedSignatories);
                            onClose();
                        }}
                        disabled={!signatairesIds}
                        tooltipText={blockingReason ?? ""}
                    >
                        {t("DocoonSignModal.Confirm")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default DocoonSignModal;
